import type { UIStrings } from '@/lib/types'
import type { PaidVhfScenario } from '@/lib/paid-vhf-config'
import { VHF_MODULES } from '@/lib/vhf-modules'
import { DEFAULT_UI } from '@/lib/ui-defaults'

/** Merged `vhfCourseShell` keys as strings (avoids `unknown` from `UIStrings` index signatures). */
export function mergeVhfCourseShellNav(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfCourseShell as Record<string, string>),
    ...(ui.vhfCourseShell as Record<string, string>),
  }
}

/** Merged `vhfQuiz` keys (fallback banner for EN question bank). */
export function mergeVhfQuiz(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfQuiz as Record<string, string>),
    ...(ui.vhfQuiz as Record<string, string>),
  }
}

/** Localized paid VHF module title for nav (falls back to `VHF_MODULES` / id). */
export function getPaidVhfModuleTitle(ui: UIStrings, moduleId: string): string {
  const fromUi = ui.vhfModuleTitles?.[moduleId]
  if (typeof fromUi === 'string' && fromUi.length > 0) return fromUi
  return VHF_MODULES.find((m) => m.id === moduleId)?.title ?? moduleId
}

/** Merged `vhfMessages` keys as strings. */
export function mergeVhfMessages(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfMessages as Record<string, string>),
    ...(ui.vhfMessages as Record<string, string>),
  }
}

/** Merged `vhfCertificate` keys (paid VHF gated certificate page). */
export function mergeVhfCertificate(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfCertificate as Record<string, string>),
    ...(ui.vhfCertificate as Record<string, string>),
  }
}

/** Merged `vhfCheckout` keys (paid VHF checkout / success / cancel). */
export function mergeVhfCheckout(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfCheckout as Record<string, string>),
    ...(ui.vhfCheckout as Record<string, string>),
  }
}

/** Merged `vhfOverview` keys (paid VHF course overview page). */
export function mergeVhfOverview(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfOverview as Record<string, string>),
    ...(ui.vhfOverview as Record<string, string>),
  }
}

/** Merged `vhfCourseLanding` keys (paid VHF marketing landing — fallbacks for missing page frontmatter). */
export function mergeVhfCourseLanding(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfCourseLanding as Record<string, string>),
    ...(ui.vhfCourseLanding as Record<string, string>),
  }
}

/** Merged `vhfSimulator` keys (paid VHF simulator gate screens). */
export function mergeVhfSimulator(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfSimulator as Record<string, string>),
    ...(ui.vhfSimulator as Record<string, string>),
  }
}

/** Merged `vhfFinalExam` keys (paid VHF final exam page UI; not MCQ content from DB). */
export function mergeVhfFinalExam(ui: UIStrings): Record<string, string> {
  return {
    ...(DEFAULT_UI.vhfFinalExam as Record<string, string>),
    ...(ui.vhfFinalExam as Record<string, string>),
  }
}

/** Apply `ui.paidVhfM7Scenarios[id]` over canonical EN from `paid-vhf-config.ts`. */
export function mergePaidVhfM7Scenario(base: PaidVhfScenario, ui: UIStrings): PaidVhfScenario {
  const o = ui.paidVhfM7Scenarios?.[base.id]
  if (!o) return base
  return {
    ...base,
    title: typeof o.title === 'string' && o.title.trim() ? o.title : base.title,
    description: typeof o.description === 'string' && o.description.trim() ? o.description : base.description,
    transcript: typeof o.transcript === 'string' && o.transcript.trim() ? o.transcript : base.transcript,
    exampleReply:
      typeof o.exampleReply === 'string' && o.exampleReply.trim() ? o.exampleReply : base.exampleReply,
    mcqOptions:
      Array.isArray(o.mcqOptions) && o.mcqOptions.length > 0 ? o.mcqOptions : base.mcqOptions,
    issaTopics: Array.isArray(o.issaTopics) && o.issaTopics.length > 0 ? o.issaTopics : base.issaTopics,
  }
}
